import React, { useEffect, useState, useRef } from "react";

const GrnRecords = () => {
  const [vendors, setVendors] = useState([]);
  const [poCodes, setPoCodes] = useState([]);
  const [poList, setPoList] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [searchPo, setSearchPo] = useState("");
  const [searchInvoice, setSearchInvoice] = useState("");
  const [selectedPoCode, setSelectedPoCode] = useState("");
  const [searchSkuCode, setSearchSkuCode] = useState("");
  const [searchSkuName, setSearchSkuName] = useState("");
  const [highlightedPo, setHighlightedPo] = useState(null);
  const [filteredPoList, setFilteredPoList] = useState([]);
  const [skuMatches, setSkuMatches] = useState([]);
  const [currentMatchIndex, setCurrentMatchIndex] = useState(-1);
  const skuSearchTimer = useRef(null);
  const [expandedPo, setExpandedPo] = useState(null);
  const [poDetails, setPoDetails] = useState({});
  const [loadingPoList, setLoadingPoList] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState({});
  const [selectedPoType, setSelectedPoType] = useState("");
  const [grnChains, setGrnChains] = useState({});
  const [expandedPending, setExpandedPending] = useState(false);
  const [matchedSkuPoCodes, setMatchedSkuPoCodes] = useState([]);  
  const [expandedInvoice, setExpandedInvoice] = useState(null);
  const [matchedInvoicePoCodes, setMatchedInvoicePoCodes] = useState([]);
  const [isDark, setIsDark] = useState(
    document.documentElement.getAttribute("data-mui-color-scheme") === "dark"
  );

  const poRefs = useRef({});
  const poRowRefs = useRef({});
  const poDetailsCache = useRef({});

  useEffect(() => {
    const observer = new MutationObserver(() => {
      const mode =
        document.documentElement.getAttribute("data-mui-color-scheme") === "dark";
      setIsDark(mode);
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-mui-color-scheme"],
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    loadVendors();
    loadPoCodes();
    loadAllPoList();
  }, []);

  useEffect(() => {
    filterPOs();
  }, [
    selectedVendor,
    selectedMonth,
    searchPo,
    searchInvoice,
    selectedPoCode,
    selectedPoType,
    searchSkuCode,
    searchSkuName,
    poDetails,
    matchedSkuPoCodes,
    matchedInvoicePoCodes,
  ]);

  useEffect(() => {
    const searchSku = async () => {

      if (
        !searchSkuCode &&
        !searchSkuName
      ) {

        setMatchedSkuPoCodes([]);
        filterPOs();
        return;
      }

      try {

        setLoadingPoList(true);

        const res = await fetch(
          `${
            process.env.REACT_APP_API_URL
          }/api?type=search-po-by-sku&skuCode=${encodeURIComponent(
            searchSkuCode
          )}&skuName=${encodeURIComponent(
            searchSkuName
          )}`
        );

        const data = await res.json();

        setMatchedSkuPoCodes(
          Array.isArray(data)
            ? data.map((p) => p.id)
            : []
        );

      } catch (err) {

        console.error(
          "SKU search error:",
          err
        );

      } finally {

        setLoadingPoList(false);
      }
    };

    const delayDebounce = setTimeout(
      searchSku,
      300
    );

    return () =>
      clearTimeout(delayDebounce);

  }, [searchSkuCode, searchSkuName]);

  useEffect(() => {
    if (!searchSkuCode && !searchSkuName) {
      setHighlightedPo(null);
      setSkuMatches([]);
      setCurrentMatchIndex(-1);
    }
  }, [searchSkuCode, searchSkuName]);

  const loadVendors = async () => {
    try {
      setLoadingPoList(true);
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api?type=vendors`,
      );
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      const data = await res.json();
      setVendors(Array.isArray(data) ? data : []);
      // console.log("Vendors:", data);
    } catch (error) {
      console.error("Error fetching vendors:", error);
      setVendors([]);
    } finally {
      setLoadingPoList(false);
    }
  };

  const loadPoCodes = async () => {
    try {
      setLoadingPoList(true);
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api?type=po-codes`,
      );
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      const data = await res.json();
      setPoCodes(Array.isArray(data) ? data : []);
      // console.log("PO Codes:", data);
    } catch (error) {
      console.error("Error fetching PO codes:", error);
      setPoCodes([]);
    } finally {
      setLoadingPoList(false);
    }
  };

  const loadAllPoList = async () => {
    try {
      setLoadingPoList(true);
      const url = `${process.env.REACT_APP_API_URL}/api?type=po-list`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      const data = await res.json();
      // console.log("API Response for PO List:", data);
      setPoList(Array.isArray(data) ? data : []);
      setFilteredPoList(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching PO list:", error);
      setPoList([]);
      setFilteredPoList([]);
    } finally {
      setLoadingPoList(false);
    }
  };

  const filterPOs = () => {
    if (!poList || poList.length === 0) {
      setFilteredPoList([]);
      return;
    }

    let filtered = [...poList];

    // Vendor Filter
    if (selectedVendor) {
      filtered = filtered.filter(
        (po) => po.vendorCode?.toLowerCase() === selectedVendor.toLowerCase(),
      );
    }

    // Month Filter (yyyy-mm)
    if (selectedMonth) {
      const [year, month] = selectedMonth.split("-");
      filtered = filtered.filter((po) => {
        if (!po.poCreatedDate) return false;
        const date = new Date(po.poCreatedDate);
        return (
          date.getFullYear().toString() === year &&
          (date.getMonth() + 1).toString().padStart(2, "0") === month
        );
      });
    }

    // PO Code Dropdown
    if (selectedPoCode) {
      filtered = filtered.filter(
        (po) => po.poCode?.toLowerCase() === selectedPoCode.toLowerCase(),
      );
    }

    // PO Type Filter
    if (selectedPoType) {
      filtered = filtered.filter((po) => po.poType === selectedPoType);
    }

    // Invoice Number Filter
    // if (searchInvoice) {
    //   const query = searchInvoice.toLowerCase();

    //   filtered = filtered.filter((po) => {
    //     const cached = poDetailsCache.current[po.id];

    //     if (!cached) {
    //       return false;
    //     }

    //     if (po.poType === "LSL") {
    //       return (cached.invoices || []).some((inv) =>
    //         normalize(inv.invoiceNumber).includes(normalize(query)),
    //       );
    //     }

    //     const invoiceNo =
    //       cached?.invoiceNumber || cached?.invoices?.[0]?.invoiceNumber || "";

    //     return normalize(invoiceNo).includes(normalize(query));
    //   });
    // }

    if (searchInvoice.trim()) {
      filtered = filtered.filter((po) =>
        matchedInvoicePoCodes.some(
          (id) => Number(id) === Number(po.id)
        )
      );
    }

    // Search PO Number
    if (searchPo) {
      const query = searchPo.toLowerCase();
      filtered = filtered.filter(
        (po) =>
          po.poCode?.toLowerCase().includes(query) ||
          po.companyName?.toLowerCase().includes(query) ||
          po.vendorCode?.toLowerCase().includes(query),
      );
    }

    if (
      matchedSkuPoCodes.length > 0
    ) {

      filtered = filtered.filter(
        (po) =>
          matchedSkuPoCodes.includes(
            po.id
          )
      );
    }
    setFilteredPoList(filtered);
  };

  const viewPoDetails = async (
    po,
    shouldScroll = false,
    permanentHighlight = false,
  ) => {
    // console.log("viewPoDetails received:", po);
    // console.trace();

    const poId = po.id;
    const poCode = po.poCode;

    if (!poCode) {
      console.error("No poCode provided for viewPoDetails");
      return;
    }

    if (expandedPo === po.id) {
      setExpandedPo(null);
      setExpandedInvoice(null);
      return;
    }

    // Scroll to the PO summary row immediately
    const summaryEl = poRowRefs.current[po.id];
    if (shouldScroll && summaryEl) {
      summaryEl.scrollIntoView({ behavior: "smooth", block: "center" });
    }

    const poType = po?.poType;

    /* ===================== USE CACHE FIRST ===================== */
    if (poDetailsCache.current[po.id]) {
      const cached = poDetailsCache.current[po.id];

      if (poType === "LSL") {
        const invoices = cached.invoices || [];
        const pendingItems = cached.pendingItems || [];

        poDetailsCache.current[po.id] = {
          ...cached,
          invoices,
          pendingItems,
        };

        setGrnChains((prev) => ({
          ...prev,
          [po.id]: invoices,
        }));

        setExpandedPo(po.id);
        setExpandedInvoice({
          poCode: null,
          invoiceId: null,
        });

        if (permanentHighlight) setHighlightedPo(po.id);

        if (shouldScroll) {
          setTimeout(() => {
            const detailEl = poRefs.current[po.id];
            if (detailEl)
              detailEl.scrollIntoView({ behavior: "smooth", block: "center" });
          }, 100);
        }

        return;
      } else {
        const rows =
          cached?.rows ||
          (Array.isArray(cached)
            ? cached
            : cached?.invoices?.[0]?.items || cached?.items || []);

        setPoDetails((prev) => ({ ...prev, [po.id]: rows }));
        setExpandedPo(po.id);

        if (permanentHighlight) setHighlightedPo(poCode);

        if (shouldScroll) {
          setTimeout(() => {
            const detailEl = poRefs.current[poCode];
            if (detailEl)
              detailEl.scrollIntoView({ behavior: "smooth", block: "center" });
          }, 100);
        }

        return;
      }
    }

    /* ===================== FETCH FROM API ===================== */
    setLoadingDetails((prev) => ({ ...prev, [po.id]: true }));
    setPoDetails((prev) => ({ ...prev, [po.id]: [] }));

    try {
      const url = `${process.env.REACT_APP_API_URL}/api?type=get-po-details&poId=${encodeURIComponent(po.id)}`;
      // console.log(`Fetching PO details: ${url}`);

      const res = await fetch(url);
      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(
          `HTTP error! status: ${res.status}, body: ${errorText}`,
        );
      }

      const contentType = res.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const errorText = await res.text();
        throw new Error(`Expected JSON, got ${contentType}: ${errorText}`);
      }

      const raw = await res.json();
      const normalized = raw;

      poDetailsCache.current[po.id] = normalized;

      if (poType === "LSL") {
        const invoices = normalized?.invoices || [];
        const pendingItems = normalized?.pendingItems || [];

        poDetailsCache.current[po.id] = {
          ...normalized,
          invoices,
          pendingItems,
        };

        setGrnChains((prev) => ({
          ...prev,
          [po.id]: invoices,
        }));

        setExpandedPo(po.id);
        setExpandedInvoice({
          poCode: null,
          invoiceId: null,
        });
      } else {
        // Fresh / Normal PO
        const rows =
          normalized?.rows ||
          (Array.isArray(normalized)
            ? normalized
            : normalized?.invoices?.[0]?.items || normalized?.items || []);

        setPoDetails((prev) => ({ ...prev, [po.id]: rows }));
        setExpandedPo(po.id);
      }

      if (permanentHighlight) {
        setHighlightedPo(poCode);
      }

      if (shouldScroll) {
        setTimeout(() => {
          const detailEl = poRefs.current[poCode];
          if (detailEl)
            detailEl.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 100);
      }
    } catch (error) {
      console.error(`Error fetching PO details for ${poCode}:`, error);
      setPoDetails((prev) => ({ ...prev, [po.id]: [] }));
    } finally {
      setLoadingDetails((prev) => ({ ...prev, [po.id]: false }));
    }
  };

  const findSkuMatchesAcrossPos = async (skuCode, skuName) => {
    const code = skuCode?.trim().toLowerCase() || "";
    const name = skuName?.trim().toLowerCase() || "";

    if (!code && !name) {
      setSkuMatches([]);
      setCurrentMatchIndex(-1);
      return [];
    }

    const matches = [];

    for (const po of poList) {
      const poCode = po.poCode;

      const details = poDetailsCache.current[po.id] || poDetails[po.id] || [];

      if (!details) continue;

      const items = Array.isArray(details)
        ? details
        : details?.invoices?.flatMap((inv) => inv.items) || [];

      if (items.length === 0) continue;

      const found = items.some(
        (item) =>
          (code && item.skuCode?.toLowerCase().includes(code)) ||
          (name && item.skuName?.toLowerCase().includes(name)),
      );

      if (found) matches.push(po.id);
    }

    setSkuMatches(matches);
    setCurrentMatchIndex(matches.length ? 0 : -1);

    // console.log(
    //   `🔍 Found ${matches.length} PO(s) matching SKU filter`,
    //   matches,
    // );

    return matches;
  };

  const goToMatchAtIndex = async (index) => {
    if (!skuMatches || skuMatches.length === 0) return;

    const safeIndex =
      ((index % skuMatches.length) + skuMatches.length) %
      skuMatches.length;

    const targetPoId = skuMatches[safeIndex];

    const targetPo = poList.find(
      (p) => p.id === targetPoId
    );

    if (!targetPo) return;

    setCurrentMatchIndex(safeIndex);
    setHighlightedPo(null);

    await viewPoDetails(targetPo, true, true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr || dateStr === "") return "-";
    const d = new Date(dateStr);
    return isNaN(d) ? "-" : d.toLocaleDateString();
  };

  const formatNumber = (value) => {
    return value === null || value === undefined ? "0" : value.toString();
  };

  const renderMrpIndicator = (ordered, received) => {
    const o = Number(ordered);
    const r = Number(received);

    if (isNaN(o) || isNaN(r)) {
      return {
        style: {},
        indicator: null,
      };
    }

    if (r > o) {
      return {
        style: {
          backgroundColor: " #166534",
          color: "#dcfce7",
          fontWeight: "600",
        },
        indicator: <span style={{ marginLeft: "6px" }}>⬆️</span>,
      };
    }

    if (r < o && r > null) {
      return {
        style: {
          backgroundColor: "#ac3636",
          color: "#fee2e2",
          fontWeight: "600",
        },
        indicator: <span style={{ marginLeft: "6px" }}>⬇️</span>,
      };
    }

    return {
      style: {},
      indicator: null,
    };
  };

  const getTotalReceivedQty = (poId) => {
    const cached = poDetailsCache.current[poId];

    if (!cached) return 0;

    // LSL PO
    if (cached.invoices && cached.invoices.length > 0) {
      return cached.invoices.reduce((total, inv) => {
        const invTotal = (inv.items || []).reduce(
          (sum, item) => sum + Number(item.receivedQty || 0),
          0,
        );
        return total + invTotal;
      }, 0);
    }

    // Pending GRN Items
    if (cached.pendingItems && cached.pendingItems.length > 0) {
      return cached.pendingItems.reduce(
        (sum, item) => sum + Number(item.receivedQty || 0),
        0,
      );
    }

    // Fresh PO
    const rows = cached?.rows || [];
    return rows.reduce((sum, item) => sum + Number(item.receivedQty || 0), 0);
  };

  const normalize = (val) => (val || "").toString().trim().toLowerCase();

  const hasKitSkuData = (rows = []) => {
    if (!Array.isArray(rows)) {
      return false;
    }

    return rows.some((r) => (
      r?.childSkuCode != null ||
      r?.childSkuName != null ||
      r?.multiplier != null ||
      r?.convertedQty != null ||
      r?.childMrp != null ||
      r?.parentBeforeQty != null ||
      r?.parentAfterQty != null ||
      r?.childBeforeQty != null ||
      r?.childAfterQty != null
    ));
  };

  useEffect(() => {
    const invoice = searchInvoice
      .replace(/\r/g, "")
      .replace(/\n/g, "")
      .trim();

    if (!invoice) {
      setMatchedInvoicePoCodes([]);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const currentSearch = invoice;

        // console.log(
        //   "🔍 Searching Invoice:",
        //   JSON.stringify(currentSearch),
        //   currentSearch.length
        // );

        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/api?type=search-po-by-invoice&invoiceNumber=${encodeURIComponent(currentSearch)}`
        );

        const data = await res.json();

        if (
          currentSearch !==
          searchInvoice
            .replace(/\r/g, "")
            .replace(/\n/g, "")
            .trim()
        ) {
          // console.log(
          //   "⏭️ Ignoring stale response:",
          //   currentSearch
          // );
          return;
        }

        const matchedIds = Array.isArray(data)
          ? data.map((p) => Number(p.id))
          : [];

        // console.log(
        //   "✅ Invoice Search Result:",
        //   currentSearch,
        //   matchedIds
        // );

        setMatchedInvoicePoCodes(matchedIds);

      } catch (err) {
        console.error("❌ Invoice search error:", err);
        setMatchedInvoicePoCodes([]);
      }
    }, 300);

    return () => clearTimeout(timer);

  }, [searchInvoice]);

  return (
    <div
      style={{
        padding: "10px 30px",
        background: isDark ? "#0f172a" : "#ECF0F3",
        color: isDark ? "#e5e7eb" : "#111827",
        minHeight: "100vh",
      }}
    >
      <div style={{ marginBottom: "25px" }}>
        <h2
          style={{
            fontSize: "26px",
            fontWeight: "700",
            color: isDark ? "#f9fafb" : "#424141",
          }}
        >
          📦 GRN Records
        </h2>

        <p style={{ color: isDark ? "#9ca3af" : "#6b7280" , marginTop: "4px" }}>
          Track all GRN records, invoices and PO deliveries
        </p>
      </div>

      {/* FILTER CARD */}
      <div
        style={{
          background: isDark ? "#1e293b" : "#ffffff",
          color: isDark ? "#e5e7eb" : "#111827",
          padding: "15px 20px",
          borderRadius: "14px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
          marginBottom: "25px",
        }}
      >
        {/* FILTER ROW 1 */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: "20px",
            marginBottom: "15px",
            alignItems: "center",
          }}
        >
          {/* Vendor */}
          <select
            value={selectedVendor}
            onChange={(e) => setSelectedVendor(e.target.value)}
            className="reminder-input"
          >
            <option value="">Choose Vendor</option>
            {vendors.map((v) => (
              <option key={v.vendorCode} value={v.vendorCode}>
                {v.companyName
                  ? `${v.companyName} (${v.vendorCode})`
                  : `Unknown (${v.vendorCode})`}
              </option>
            ))}
          </select>

          {/* PO Code */}
          <select
            value={selectedPoCode}
            onChange={(e) => setSelectedPoCode(e.target.value)}
            className="reminder-input"
          >
            <option value="">Select PO Code</option>
            {poCodes.map((p) => (
              <option key={p.id} value={p.poCode}>
                {p.poCode}
              </option>
            ))}
          </select>

          {/* PO Type */}
          <select
            value={selectedPoType}
            onChange={(e) => setSelectedPoType(e.target.value)}
            className="reminder-input"
          >
            <option value="">PO Type</option>
            <option value="Fresh">Fresh</option>
            <option value="LSL">Low Shelf Life</option>
          </select>

          {/* Month */}
          <div>
            <input
              type="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="reminder-input"
            />
          </div>
        </div>

        {/* FILTER ROW 2 */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: "20px",
          }}
        >
          <input
            type="text"
            placeholder="🔍 Search PO Number"
            value={searchPo}
            onChange={(e) => setSearchPo(e.target.value)}
            className="reminder-input"
          />

          <input
            type="text"
            placeholder="🔍 Search Invoice Number"
            value={searchInvoice}
            onChange={(e) => {
              setSearchInvoice(e.target.value);
            }}
            className="reminder-input"
          />

          <input
            type="text"
            placeholder="🔍 Search SKU Code"
            value={searchSkuCode}
            onChange={(e) => {
              const val = e.target.value;
              setSearchSkuCode(val);

              if (skuSearchTimer.current) clearTimeout(skuSearchTimer.current);

              skuSearchTimer.current = setTimeout(() => {
                findSkuMatchesAcrossPos(val, searchSkuName);
              }, 250);
            }}
            onKeyDown={async (e) => {
              if (e.key === "Enter") {
                if (skuMatches.length === 0) {
                  await findSkuMatchesAcrossPos(searchSkuCode, searchSkuName);
                  if (skuMatches.length > 0) await goToMatchAtIndex(0);
                } else {
                  await goToMatchAtIndex(currentMatchIndex + 1);
                }
              }
            }}
            className="reminder-input"
          />

          <input
            type="text"
            placeholder="🔍 Search SKU Name"
            value={searchSkuName}
            onChange={(e) => {
              const val = e.target.value;
              setSearchSkuName(val);

              if (skuSearchTimer.current) clearTimeout(skuSearchTimer.current);

              skuSearchTimer.current = setTimeout(() => {
                findSkuMatchesAcrossPos(searchSkuCode, val);
              }, 250);
            }}
            onKeyDown={async (e) => {
              if (e.key === "Enter") {
                if (skuMatches.length === 0) {
                  await findSkuMatchesAcrossPos(searchSkuCode, searchSkuName);
                  if (skuMatches.length > 0) await goToMatchAtIndex(0);
                } else {
                  await goToMatchAtIndex(currentMatchIndex + 1);
                }
              }
            }}
            className="reminder-input"
          />
        </div>
      </div>

      <div
        style={{
          background: isDark ? "#1e293b" : "#ffffff",
          borderRadius: "14px",
          overflow: "hidden",
          boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
          width: "100%",
          maxWidth: "100%"
        }}
      >
        <div
          style={{
            // overflowX: "auto",
            width: "100%",
            maxWidth: "100%",
            // WebkitOverflowScrolling: "touch",
            background: isDark ? "#1e293b" : "#ffffff",
            border: isDark ? "1px solid #334155" : "1px solid #e5e7eb",
            borderRadius: "8px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            overflow: "hidden"
          }}
        >
          <table
            style={{
              width: "100%",
              tableLayout: "auto",
              borderCollapse: "collapse",
              fontSize: "14px",
            }}
          >
            <thead style={{ backgroundColor: isDark ? "#4f9f92" : "#75bfb0", }}>
              <tr>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "center",
                    fontWeight: "600",
                    fontSize: "15px",
                  }}
                >
                  S.N.
                </th>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "left",
                    fontWeight: "600",
                    fontSize: "15px",
                  }}
                >
                  PO Code
                </th>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "left",
                    fontWeight: "600",
                    fontSize: "15px",
                  }}
                >
                  PO Type
                </th>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "left",
                    fontWeight: "600",
                    fontSize: "15px",
                  }}
                >
                  PO Date
                </th>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "left",
                    fontWeight: "600",
                    fontSize: "15px",
                  }}
                >
                  Vendor Name
                </th>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "left",
                    fontWeight: "600",
                    fontSize: "15px",
                  }}
                >
                  Invoice Received On
                </th>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "left",
                    fontWeight: "600",
                    fontSize: "15px",
                  }}
                >
                  GRN Updated At
                </th>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "center",
                    fontWeight: "600",
                    fontSize: "15px",
                  }}
                >
                  View Details
                </th>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "left",
                    fontWeight: "600",
                    fontSize: "15px",
                  }}
                >
                  PO Status
                </th>
              </tr>
            </thead>
            <tbody>
              {loadingPoList ? (
                <tr>
                  <td
                    colSpan="9"
                    style={{
                      textAlign: "center",
                      padding: "16px",
                      color: "#6b7280",
                    }}
                  >
                    <div className="loader"></div>
                  </td>
                </tr>
              ) : filteredPoList.length === 0 ? (
                <tr>
                  <td
                    colSpan="9"
                    style={{
                      textAlign: "center",
                      padding: "16px",
                      color: "#6b7280",
                    }}
                  >
                    No records found
                  </td>
                </tr>
              ) : (
                filteredPoList.map((po, index) => (
                  <React.Fragment key={po.id}>
                    <tr
                      style={{
                        backgroundColor: isDark
                          ? index % 2 === 0
                            ? "#1e293b"
                            : "#0f172a"
                          : index % 2 === 0
                          ? "#fff"
                          : "#f9fafb",
                        color: isDark ? "#e5e7eb" : "#111827",
                        transition: "background-color 0.2s",
                      }}
                      onMouseOver={(e) =>
                        (e.currentTarget.style.backgroundColor = isDark ? "#334155" : "#dbeafe")
                      }
                      onMouseOut={(e) =>
                        (e.currentTarget.style.backgroundColor = isDark
                          ? index % 2 === 0
                            ? "#1e293b"
                            : "#0f172a"
                          : index % 2 === 0
                          ? "#fff"
                          : "#f9fafb")
                      }
                    >
                      <td style={{ padding: "12px", textAlign: "center" }}>{index + 1}</td>
                      <td style={{ padding: "12px" }}>{po.poCode}</td>
                      <td style={{ padding: "12px" }}>{po.poType || "-"}</td> 
                      <td style={{ padding: "12px" }}>
                        {formatDate(po.poCreatedDate)}
                      </td>
                      <td style={{ padding: "12px" }}>
                        {po.companyName
                          ? `${po.companyName} (${po.vendorCode || ""})`
                          : "Unknown Vendor"}
                      </td>
                      <td style={{ padding: "12px" }}>
                        {formatDate(po.grnReceivedDate)}
                      </td>
                      <td style={{ padding: "12px" }}>
                        {formatDate(po.grnUpdatedAt)}
                      </td>
                      <td style={{ padding: "12px", textAlign: "center" }}>
                        <button
                          onClick={() => viewPoDetails(po, false, false)}
                          style={{
                            backgroundColor: "#75BFB0",
                            color: "#fff",
                            padding: "4px 16px",
                            borderRadius: "8px",
                            transition: "background-color 0.2s",
                          }}
                          onMouseOver={(e) =>
                            (e.currentTarget.style.backgroundColor = "#424141")
                          }
                          onMouseOut={(e) =>
                            (e.currentTarget.style.backgroundColor = "#75BFB0")
                          }
                        >
                          {expandedPo === po.id ? "Hide" : "View"}
                        </button>
                      </td>
                      <td style={{ padding: "12px" }}>
                        {po.closed === 1 ? (
                          <span style={{ color: "red", fontWeight: "bold" }}>
                            Closed
                          </span>
                        ) : (
                          <span style={{ color: isDark ? "fff" : "green", fontWeight: "bold" }}>
                            Open until: {formatDate(po.closedOnDate)}
                          </span>
                        )}
                      </td>
                    </tr>

                    {expandedPo === po.id && (
                      <tr ref={(el) => (poRefs.current[po.poCode] = el)}>
                        <td
                          colSpan="9"
                          style={{
                            backgroundColor:
                              highlightedPo === po.id
                                ? "#ffeb99"
                                : isDark ? "#1e293b" : "#f2f3f5",
                                
                            transition: "background-color 0.5s ease",
                            padding: "16px",
                            paddingTop: "0px",
                          }}
                        >
                          {(() => {
                            const pendingItems =
                              poDetailsCache.current[po.id]?.pendingItems ||
                              [];

                            const invoices = Array.isArray(grnChains[po.id])
                              ? grnChains[po.id]
                              : [];

                            return (
                              <>
                                <h3
                                  style={{
                                    fontWeight: "600",
                                    fontSize: "1.125rem",
                                    marginBottom: "12px",
                                    color: isDark ? "#ffffff" : "#1e293b",
                                  }}
                                >
                                  PO Details - {po.poCode} | PO Type -{" "}
                                  {po.poType}
                                  {po.poType !== "LSL" &&
                                    (() => {
                                      const cached =
                                        poDetailsCache.current[po.id];
                                      const invoiceNo =
                                        cached?.invoiceNumber ||
                                        cached?.invoices?.[0]?.invoiceNumber;

                                      return invoiceNo
                                        ? ` | Invoice No - ${invoiceNo}`
                                        : "";
                                    })()}
                                  {" | Total Received Qty - "}
                                  {getTotalReceivedQty(po.id)}
                                </h3>

                                {/* ===================== LSL PO VIEW ===================== */}
                                {po.poType === "LSL" ? (
                                  <div style={{ marginBottom: "16px" }}>
                                    {/* 🔹 INVOICE CHAINS */}
                                    {invoices.map((inv, index) => (
                                      <div
                                        key={inv.grnId}
                                        style={{
                                          marginBottom: "18px",
                                          border: "1px solid #e5e7eb",
                                          borderRadius: "8px",
                                          background: isDark ? "#1e293b" : "#ffffff",
                                          boxShadow:
                                            "0 1px 4px rgba(0,0,0,0.08)",
                                        }}
                                      >
                                        {/* Invoice Header */}
                                        <div
                                          style={{
                                            padding: "10px 14px",
                                            background: "#75BFB0",
                                            color: "#fff",
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            borderRadius: "8px 8px 0 0",
                                          }}
                                        >
                                          <span style={{ fontWeight: "600" }}>
                                            {index + 1}. Invoice No:{" "}
                                            {inv.invoiceNumber}
                                          </span>

                                          <button
                                            onClick={() =>
                                              setExpandedInvoice(
                                                expandedInvoice === inv.grnId
                                                  ? null
                                                  : inv.grnId,
                                              )
                                            }
                                            style={{
                                              backgroundColor: isDark ? "#1e293b" : "#fff",
                                              color: isDark ? "#fff" : "#1e293b",
                                              padding: "6px 16px",
                                              borderRadius: "6px",
                                              fontSize: "0.8rem",
                                              fontWeight: "600",
                                              border: "none",
                                              cursor: "pointer",
                                              maxWidth: "110px",
                                              height: "30px",
                                            }}
                                          >
                                            {expandedInvoice === inv.grnId
                                              ? "Hide Details"
                                              : "View Details"}
                                          </button>
                                        </div>

                                        {/* Invoice Items */}
                                        {expandedInvoice === inv.grnId && (() => {

                                          const hasKitSku = hasKitSkuData(
                                            inv.items || []
                                          );

                                          return (
                                          <div
                                            style={{
                                                  overflowX: "auto",
                                                  overflowY: "hidden",
                                                  width: "100%",
                                                  maxWidth: "calc(100vw - 180px)",
                                                  WebkitOverflowScrolling: "touch",
                                                  marginTop: "10px",
                                                  border: isDark
                                                    ? "1px solid #334155"
                                                    : "1px solid #e5e7eb",
                                            }}
                                          >
                                            <table
                                              style={{
                                                width: "max-content",
                                                minWidth: "1700px",
                                                borderCollapse: "collapse",
                                              }}
                                            >
                                              <thead style={{ backgroundColor: "#75BFB0", color: "#fff" }}>
                                                <tr>
                                                  <th style={{ padding: "10px", textAlign: "left" }}>S.N.</th>
                                                  <th style={{ padding: "10px", textAlign: "left" }}>SKU Code</th>
                                                  <th style={{ padding: "10px", textAlign: "left" }}>SKU Name</th>

                                                {hasKitSku && (
                                                  <>
                                                    <th style={{ padding: "10px", textAlign: "left", minWidth: "140px" }}>Child SKU</th>
                                                    <th style={{ padding: "10px", textAlign: "left", minWidth: "180px" }}>Child SKU Name</th>
                                                  </>
                                                )}
                                                  <th style={{ padding: "10px", textAlign: "center" }}>Expected Qty</th>
                                                  <th style={{ padding: "10px", textAlign: "center" }}>Expected Shelf Life %</th>
                                                  <th style={{ padding: "10px", textAlign: "center" }}>Ordered MRP</th>                                                  
                                                  
                                                {hasKitSku ? (
                                                  <>
                                                    <th style={{ padding: "10px", textAlign: "center" }}>Parent Before Qty</th>
                                                    <th style={{ padding: "10px", textAlign: "center" }}>Parent Received Qty</th>
                                                    <th style={{ padding: "10px", textAlign: "center" }}>Parent After Qty</th>
                                                    <th style={{ padding: "10px", textAlign: "center" }}>Child Before Qty</th>
                                                    <th style={{ padding: "10px", textAlign: "center", minWidth: "110px" }}>Multiplier</th>
                                                    <th style={{ padding: "10px", textAlign: "center", minWidth: "130px" }}>Converted Qty</th>
                                                    <th style={{ padding: "10px", textAlign: "center" }}>Child After Qty</th>
                                                    <th style={{ padding: "10px", textAlign: "center", minWidth: "120px" }}>Child MRP</th>
                                                  </>
                                                ) : (
                                                  <>
                                                      <th style={{ padding: "10px", textAlign: "center" }}>Before Qty</th>
                                                      <th style={{ padding: "10px", textAlign: "center" }}>Received Qty</th>
                                                      <th style={{ padding: "10px", textAlign: "center" }}>After Qty</th>
                                                    </>
                                                  )}

                                                  <th style={{ padding: "10px", textAlign: "center" }}>Received MRP</th>
                                                  <th style={{ padding: "10px", textAlign: "center" }}>Received Shelf Life %</th>
                                                  <th style={{ padding: "10px", textAlign: "center" }}>Damaged</th>
                                                  <th style={{ padding: "10px", textAlign: "center" }}>Expiry Date</th>
                                                </tr>
                                              </thead>

                                              <tbody>
                                                {inv.items.map((d, i) => (
                                                  <tr key={i} style={{
                                                      backgroundColor: isDark
                                                        ? i % 2 === 0
                                                          ? "#1e293b"
                                                          : "#0f172a"
                                                        : i % 2 === 0
                                                        ? "#ffffff"
                                                        : "#f8f9fa",
                                                      color: isDark ? "#e5e7eb" : "#111827",
                                                    }}
                                                  >
                                                    <td style={{ padding: "10px", textAlign: "center" }}>{i + 1}</td>
                                                    <td style={{ padding: "10px" }}>{d.skuCode}</td>
                                                    <td style={{ padding: "10px", width: "700px", wordBreak: "break-word", whiteSpace: "normal" }}>{d.skuName}</td>
                                                    
                                                  {hasKitSkuData(
                                                    inv.items || poDetails[po.id] || []
                                                  ) && (
                                                    <>                                                    
                                                      <td style={{ padding: "10px" }}>{d.childSkuCode || "-"}</td>
                                                      <td style={{ padding: "10px" }}>{d.childSkuName || "-"}</td>
                                                    </>
                                                  )}

                                                    <td style={{ padding: "10px", textAlign: "center" }}>{d.expectedQty}</td>
                                                    <td style={{ padding: "10px", textAlign: "center" }}>
                                                      {d.shelfLifePercent !=
                                                      null
                                                        ? (() => {
                                                            const v = Number(
                                                              d.shelfLifePercent,
                                                            );
                                                            return `${Number.isInteger(v) ? v : v.toFixed(2)}%`;
                                                          })()
                                                        : "-"}
                                                    </td>
                                                    <td style={{ padding: "10px", textAlign: "center" }}>₹{d.mrp}</td>

                                                    {hasKitSku ? (
                                                      <>
                                                        <td style={{ padding: "10px", textAlign: "center" }}>{d.parentBeforeQty ?? "NA"}</td>
                                                        <td style={{ padding: "10px", textAlign: "center" }}>{d.receivedQty ?? 0}</td>
                                                        <td style={{ padding: "10px", textAlign: "center" }}>{d.parentAfterQty ?? "NA"}</td>
                                                        <td style={{ padding: "10px", textAlign: "center" }}>{d.childBeforeQty ?? "NA"}</td>
                                                        <td style={{ padding: "10px", textAlign: "center", fontWeight: "600", color: "#2563eb" }}>{d.multiplier ? `x${d.multiplier}` : "-"}</td>
                                                        <td style={{ padding: "10px", textAlign: "center", color: "#16a34a", fontWeight: "700" }}>{d.convertedQty ?? "-"}</td>
                                                        <td style={{ padding: "10px", textAlign: "center" }}>{d.childAfterQty ?? "NA"}</td>
                                                        <td style={{ padding: "10px", textAlign: "center" }}>{d.childMrp ? `₹${d.childMrp}` : "-"}</td>
                                                      </>
                                                    ) : (
                                                      <>
                                                        <td style={{ padding: "10px", textAlign: "center" }}>{d.beforeQty ?? "NA"}</td>
                                                        <td style={{ padding: "10px", textAlign: "center" }}>{d.receivedQty ?? 0}</td>
                                                        <td style={{ padding: "10px", textAlign: "center" }}>{d.afterQty ?? "NA"}</td>
                                                      </>
                                                    )}

                                                    <td
                                                      style={{
                                                        padding: "10px",
                                                        textAlign: "center",
                                                        ...renderMrpIndicator(
                                                          d.mrp,
                                                          d.receivedMrp,
                                                        ).style,
                                                      }}
                                                    >
                                                      ₹{d.receivedMrp}
                                                      {
                                                        renderMrpIndicator(
                                                          d.mrp,
                                                          d.receivedMrp,
                                                        ).indicator
                                                      }
                                                    </td>
                                                    <td style={{ padding: "10px", textAlign: "center" }}>
                                                      {d.receivedShelfLifePercent !=
                                                      null
                                                        ? (() => {
                                                            const v = Number(
                                                              d.receivedShelfLifePercent,
                                                            );
                                                            return `${Number.isInteger(v) ? v : v.toFixed(2)}%`;
                                                          })()
                                                        : "-"}
                                                    </td>
                                                    <td style={{ padding: "10px", textAlign: "center" }}>{d.damaged}</td>
                                                    <td style={{ padding: "10px", textAlign: "center" }}>{formatDate(d.expiryDate)}</td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </table>
                                          </div>
                                          );
                                        })()}
                                      </div>
                                    ))}

                                    {/* 🔹 PENDING GRN SECTION */}
                                    {pendingItems.length > 0 && (
                                      <div
                                        style={{
                                          marginTop: "20px",
                                          border: "1px dashed #f59e0b",
                                          borderRadius: "8px",
                                          background: "#fff7ed",
                                        }}
                                      >
                                        <div
                                          style={{
                                            padding: "10px 14px",
                                            background: "rgb(252, 149, 5)",
                                            color: "#fff",
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            borderRadius: "8px 8px 0 0",
                                          }}
                                        >
                                          <span style={{ fontWeight: "600" }}>
                                            {pendingItems.every(
                                              (item) =>
                                                (item.pendingQty ?? 0) === 0,
                                            )
                                              ? `Fully Received Items (${pendingItems.length})`
                                              : `Pending GRN Items (${pendingItems.filter((item) => (item.pendingQty ?? 0) > 0).length})`}
                                          </span>

                                          <button
                                            onClick={() =>
                                              setExpandedPending(
                                                !expandedPending,
                                              )
                                            }
                                            style={{
                                              backgroundColor: isDark ? "#1e293b" : "#fff",
                                              color: isDark ? "#fff" : "#1e293b",
                                              padding: "6px 16px",
                                              borderRadius: "6px",
                                              fontSize: "0.8rem",
                                              fontWeight: "600",
                                              border: "none",
                                              cursor: "pointer",
                                              maxWidth: "110px",
                                              height: "30px",
                                            }}
                                          >
                                            {expandedPending
                                              ? "Hide Details"
                                              : "View Details"}
                                          </button>
                                        </div>

                                        {expandedPending && (() => {

                                          const hasKitSku = hasKitSkuData(
                                            pendingItems || []
                                          );

                                          return (
                                          <div
                                            style={{
                                              overflowX: "auto",
                                              overflowY: "hidden",
                                              width: "100%",
                                              maxWidth: "calc(100vw - 180px)",
                                              WebkitOverflowScrolling: "touch",
                                              background: isDark ? "#1e293b" : "#fff",
                                              marginTop: "10px",
                                            }}
                                          >
                                            {pendingItems.length === 0 ? (
                                              <div
                                                style={{
                                                  textAlign: "center",
                                                  padding: "16px",
                                                  color: "#666",
                                                }}
                                              >
                                                No pending items
                                              </div>
                                            ) : (
                                              <table
                                                style={{
                                                  width: "max-content",
                                                  minWidth: "1700px",
                                                  tableLayout: "auto",
                                                  borderCollapse: "collapse",
                                                  background: "#fff",
                                                }}
                                              >
                                                <thead
                                                  style={{
                                                    backgroundColor: "#75BFB0",
                                                    color: "#fff",
                                                  }}
                                                >
                                                  <tr>
                                                    <th style={{ padding: "10px", textAlign: "left" }}>S.N.</th>
                                                    <th style={{ padding: "10px", textAlign: "left" }}>SKU Code</th>
                                                    <th style={{ padding: "10px", textAlign: "left" }}>SKU Name</th>

                                                    {hasKitSku && (
                                                      <>
                                                        <th style={{ padding: "10px", textAlign: "left", minWidth: "140px" }}>Child SKU</th>
                                                        <th style={{ padding: "10px", textAlign: "left", minWidth: "180px" }}>Child SKU Name</th>
                                                      </>
                                                    )}

                                                    <th style={{ padding: "10px", textAlign: "center" }}>Expected Qty</th>
                                                    <th style={{ padding: "10px", textAlign: "center" }}>Expected Shelf Life %</th>
                                                    <th style={{ padding: "10px", textAlign: "center" }}>Ordered MRP</th>                                                    

                                                    {hasKitSku ? (
                                                      <>
                                                        <th style={{ padding: "10px", textAlign: "center" }}>Parent Before Qty</th>
                                                        <th style={{ padding: "10px", textAlign: "center" }}>Parent Received Qty</th>
                                                        <th style={{ padding: "10px", textAlign: "center" }}>Parent After Qty</th>
                                                        <th style={{ padding: "10px", textAlign: "center" }}>Child Before Qty</th>
                                                        <th style={{ padding: "10px", textAlign: "center", minWidth: "110px" }}>Multiplier</th>
                                                        <th style={{ padding: "10px", textAlign: "center", minWidth: "130px" }}>Converted Qty</th>
                                                        <th style={{ padding: "10px", textAlign: "center" }}>Child After Qty</th>
                                                        <th style={{ padding: "10px", textAlign: "center", minWidth: "120px" }}>Child MRP</th>
                                                      </>
                                                    ) : (
                                                      <>
                                                        <th style={{ padding: "10px", textAlign: "center" }}>Before Qty</th>
                                                        <th style={{ padding: "10px", textAlign: "center" }}>Received Qty</th>
                                                        <th style={{ padding: "10px", textAlign: "center" }}>After Qty</th>
                                                      </>
                                                    )}

                                                    <th style={{ padding: "10px", textAlign: "center" }}>Received MRP</th>
                                                    <th style={{ padding: "10px", textAlign: "center" }}>Received Shelf Life %</th>
                                                    <th style={{ padding: "10px", textAlign: "center" }}>Damaged</th>

                                                    {pendingItems.some(
                                                      (item) =>
                                                        (item.pendingQty ?? 0) >
                                                        0,
                                                    ) && (
                                                      <th
                                                        style={{
                                                          padding: "10px",
                                                          textAlign: "center",
                                                          fontWeight: "bold",
                                                          color: "#fff",
                                                          backgroundColor:
                                                            "rgb(252, 149, 5)",
                                                        }}
                                                      >
                                                        Still Pending
                                                      </th>
                                                    )}
                                                    <th style={{ padding: "10px", textAlign: "center" }}>Expiry Date</th>
                                                  </tr>
                                                </thead>

                                                {(() => {
                                                  const hasKitSku = hasKitSkuData(
                                                    pendingItems || []
                                                  );

                                                  return (                                                
                                                <tbody>
                                                  {pendingItems.map((d, i) => {
                                                    const received = Number(
                                                      d.receivedQty ?? 0,
                                                    );
                                                    const receivedShelfLife =
                                                      Number(
                                                        d.receivedShelfLifePercent ??
                                                          0,
                                                      );
                                                    const damaged = Number(
                                                      d.damaged ?? 0,
                                                    );
                                                    const pending = Number(
                                                      d.pendingQty ?? 0,
                                                    );

                                                    return (
                                                      <tr
                                                        key={`${d.skuCode}-${i}`}
                                                        style={{
                                                          backgroundColor: isDark
                                                            ? i % 2 === 0
                                                              ? "#1e293b"
                                                              : "#0f172a"
                                                            : i % 2 === 0
                                                            ? "#ffffff"
                                                            : "#fffbeb",
                                                          color: isDark ? "#e5e7eb" : "#111827",
                                                        }}
                                                      >
                                                        <td style={{ padding: "10px" }}>{i + 1}</td>
                                                        <td style={{ padding: "10px" }}>{d.skuCode}</td>
                                                        <td style={{ padding: "10px", width: "700px", wordBreak: "break-word", whiteSpace: "normal" }}>{d.skuName}</td>
                                                        
                                                        {hasKitSku && (
                                                          <>
                                                          <td style={{ padding: "10px" }}>{d.childSkuCode || "-"}</td>
                                                          <td style={{ padding: "10px", width: "600px" }}>{d.childSkuName || "-"}</td>
                                                          </>
                                                        )}
                                                          <td style={{ padding: "10px", textAlign: "center" }}>{d.expectedQty}</td>
                                                          <td style={{ padding: "10px", textAlign: "center" }}>
                                                            {d.shelfLifePercent != null
                                                              ? `${Number(d.shelfLifePercent).toFixed(2)}%`
                                                              : "-"}
                                                          </td>
                                                          <td style={{ padding: "10px", textAlign: "center" }}>₹{d.mrp}</td>                                                        

                                                        {hasKitSku ? (
                                                          <>
                                                            <td style={{ padding: "10px", textAlign: "center" }}>{d.parentBeforeQty ?? "NA"}</td>
                                                            <td style={{ padding: "10px", textAlign: "center" }}>{received}</td>
                                                            <td style={{ padding: "10px", textAlign: "center" }}>{d.parentAfterQty ?? "NA"}</td>
                                                            <td style={{ padding: "10px", textAlign: "center" }}>{d.childBeforeQty ?? "NA"}</td>
                                                            <td style={{ padding: "10px", textAlign: "center", fontWeight: "600", color: "#2563eb" }}>{d.multiplier ? `x${d.multiplier}` : "-"}</td>
                                                            <td style={{ padding: "10px", textAlign: "center", color: "#16a34a", fontWeight: "700" }}>{d.convertedQty ?? "-"}</td>
                                                            <td style={{ padding: "10px", textAlign: "center" }}>{d.childAfterQty ?? "NA"}</td>
                                                            <td style={{ padding: "10px", textAlign: "center" }}>{d.childMrp ? `₹${d.childMrp}` : "-"}</td>
                                                          </>
                                                        ) : (
                                                          <>
                                                            <td style={{ padding: "10px", textAlign: "center" }}>{d.beforeQty ?? "NA"}</td>
                                                            <td style={{ padding: "10px", textAlign: "center" }}>{received}</td>
                                                            <td style={{ padding: "10px", textAlign: "center" }}>{d.afterQty ?? "NA"}</td>
                                                          </>
                                                        )}                                                        
                                                        
                                                        <td
                                                          style={{
                                                            padding: "10px",
                                                            textAlign: "center",
                                                            ...renderMrpIndicator(
                                                              d.mrp,
                                                              d.receivedMrp,
                                                            ).style,
                                                          }}
                                                        >
                                                          ₹{d.receivedMrp}
                                                          {
                                                            renderMrpIndicator(
                                                              d.mrp,
                                                              d.receivedMrp,
                                                            ).indicator
                                                          }
                                                        </td>
                                                        <td style={{ padding: "10px", textAlign: "center" }}>{receivedShelfLife}</td>
                                                        <td style={{ padding: "10px", textAlign: "center" }}>{damaged}</td>
                                                        
                                                        {pendingItems.some(
                                                          (item) =>
                                                            (item.pendingQty ??
                                                              0) > 0,
                                                        ) && (
                                                          <td
                                                            style={{
                                                              padding: "10px",
                                                              textAlign:
                                                                "center",
                                                              fontWeight:
                                                                "bold",
                                                              color:
                                                                pending > 0
                                                                  ? "#d97706"
                                                                  : "#16a34a",
                                                            }}
                                                          >
                                                            {pending}
                                                          </td>
                                                        )}
                                                        <td style={{ padding: "10px", textAlign: "center" }}>{formatDate(d.expiryDate)}</td>
                                                      </tr>
                                                    );
                                                  })}
                                                </tbody>
                                                  );
                                                  })()}
                                              </table>
                                            )}
                                          </div>
                                          );
                                        })()}
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  /* ===================== NORMAL / FRESH PO VIEW ===================== */
                                  <div
                                    style={{
                                      overflowX: "auto",
                                      overflowY: "hidden",
                                      width: "100%",
                                      maxWidth: "calc(100vw - 180px)",
                                      WebkitOverflowScrolling: "touch",
                                      border: "1px solid #e5e7eb",
                                      borderRadius: "10px",
                                      background: isDark ? "#1e293b" : "#ffffff",
                                      boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                                    }}
                                  >
                                    <table
                                      style={{
                                        width: "max-content",
                                        minWidth: "1700px",
                                        tableLayout: "auto",
                                        borderCollapse: "collapse",
                                      }}
                                    >
                                      
                                    {(() => {
                                      const hasKitSku = hasKitSkuData(
                                        poDetails[po.id] || []
                                      );

                                      return (
                                        <>
                                      <thead
                                        style={{
                                          backgroundColor: "#75BFB0",
                                          color: "#fff",
                                        }}
                                      >
                                        <tr>
                                          <th style={{ padding: "10px", textAlign: "center" }}>S.N.</th>
                                          <th style={{ padding: "10px", textAlign: "left" }}>SKU Code</th>
                                          <th style={{ padding: "10px", textAlign: "left" }}>SKU Name</th>
                                                                               
                                        {hasKitSku && (
                                          <>
                                            <th style={{ padding: "10px", textAlign: "left" }}>Child SKU</th>
                                            <th style={{ padding: "10px", textAlign: "left" }}>Child SKU Name</th>
                                          </>
                                        )}   
                                          <th style={{ padding: "10px", textAlign: "center" }}>Expected Qty</th>
                                          <th style={{ padding: "10px", textAlign: "center" }}>Ordered MRP</th>

                                          {hasKitSku ? (
                                              <>
                                                <th style={{ padding: "10px", textAlign: "center" }}>Parent Before Qty</th>
                                                <th style={{ padding: "10px", textAlign: "center" }}>Parent Received Qty</th>
                                                <th style={{ padding: "10px", textAlign: "center" }}>Parent After Qty</th>
                                                <th style={{ padding: "10px", textAlign: "center" }}>Child Before Qty</th>     
                                                <th style={{ padding: "10px", textAlign: "center", minWidth: "110px" }}>Multiplier</th>
                                                <th style={{ padding: "10px", textAlign: "center", minWidth: "130px" }}>Converted Qty</th>
                                                <th style={{ padding: "10px", textAlign: "center" }}>Child After Qty</th>
                                                <th style={{ padding: "10px", textAlign: "center", minWidth: "120px" }}>Child MRP</th>
                                              </>
                                            ) : (
                                              <>
                                                <th style={{ padding: "10px", textAlign: "center" }}>Before Qty</th>
                                                <th style={{ padding: "10px", textAlign: "center" }}>Received Qty</th>
                                                <th style={{ padding: "10px", textAlign: "center" }}>After Qty</th>
                                              </>
                                            )}

                                          <th style={{ padding: "10px", textAlign: "center" }}>Received MRP</th>
                                          <th style={{ padding: "10px", textAlign: "center" }}>Received Shelf Life %</th>
                                          <th style={{ padding: "10px", textAlign: "center" }}>Damaged</th>
                                          <th style={{ padding: "10px", textAlign: "center" }}>Expiry Date</th>
                                        </tr>
                                      </thead>
                                      </> 
                                    );
                                  })()}

                                    {(() => {
                                      const hasKitSku = hasKitSkuData(
                                        poDetails[po.id] || []
                                      );

                                      return (
                                        <>                                      
                                      <tbody>
                                        {(poDetails[po.id] || []).map(
                                          (d, i) => (
                                            <tr
                                              key={`${d.skuCode}-${i}`}
                                              style={{
                                                backgroundColor: isDark
                                                  ? i % 2 === 0
                                                    ? "#1e293b"
                                                    : "#0f172a"
                                                  : i % 2 === 0
                                                  ? "#ffffff"
                                                  : "#f8f9fa",
                                                color: isDark ? "#e5e7eb" : "#111827",
                                              }}
                                            >
                                              <td style={{ padding: "10px", textAlign: "center"}}>{i + 1}</td>
                                              <td style={{ padding: "10px" }}>{d.skuCode}</td>
                                              <td style={{ padding: "10px", width: "600px" }}>{d.skuName}</td>

                                            {hasKitSku && (
                                              <>
                                              <td style={{ padding: "10px" }}>{d.childSkuCode || "-"}</td>
                                              <td style={{ padding: "10px", width: "600px" }}>{d.childSkuName || "-"}</td>
                                              </>
                                            )}
                                              <td style={{ padding: "10px", textAlign: "center" }}>{d.expectedQty}</td>
                                              <td style={{ padding: "10px", textAlign: "center" }}>₹{d.mrp}</td>

                                              {hasKitSku ? (
                                                  <>
                                                    <td style={{ padding: "10px", textAlign: "center"}}>{d.parentBeforeQty ?? "NA"}</td>
                                                    <td style={{ padding: "10px", textAlign: "center"}}>{d.receivedQty ?? 0}</td>
                                                    <td style={{ padding: "10px", textAlign: "center"}}>{d.parentAfterQty ?? "NA"}</td>                                                                                                        
                                                    <td style={{ padding: "10px", textAlign: "center"}}>{d.childBeforeQty ?? "NA"}</td>
                                                    <td
                                                      style={{
                                                        padding: "10px",
                                                        textAlign: "center",
                                                        fontWeight: "600",
                                                        color: "#2563eb",
                                                      }}
                                                    >
                                                      {d.multiplier
                                                        ? `x${d.multiplier}`
                                                        : "-"
                                                      }
                                                    </td>

                                                    <td
                                                      style={{
                                                        padding: "10px",
                                                        textAlign: "center",
                                                        color: "#16a34a",
                                                        fontWeight: "700",
                                                      }}
                                                    >
                                                      {d.convertedQty || "-"}
                                                    </td>
                                                    <td style={{ padding: "10px", textAlign: "center"}}>{d.childAfterQty ?? "NA"}</td>
                                                    <td style={{ padding: "10px", textAlign: "center" }}>
                                                      {d.childMrp
                                                        ? `₹${d.childMrp}`
                                                        : "-"
                                                      }
                                                    </td>
                                                  </>
                                                ) : (
                                                  <>
                                                    <td style={{ padding: "10px", textAlign: "center"}}>{d.beforeQty ?? "NA"}</td>
                                                    <td style={{ padding: "10px", textAlign: "center"}}>{d.receivedQty ?? 0}</td>
                                                    <td style={{ padding: "10px", textAlign: "center"}}>{d.afterQty ?? "NA"}</td>
                                                  </>
                                                )}
                                              
                                              <td
                                                style={{
                                                  padding: "10px",
                                                  textAlign: "center",
                                                  ...renderMrpIndicator(
                                                    d.mrp,
                                                    d.receivedMrp,
                                                  ).style,
                                                }}
                                              >
                                                ₹{d.receivedMrp}
                                                {
                                                  renderMrpIndicator(
                                                    d.mrp,
                                                    d.receivedMrp,
                                                  ).indicator
                                                }
                                              </td>
                                              <td style={{ padding: "10px", textAlign: "center" }}>
                                                {d.receivedShelfLifePercent !=
                                                null
                                                  ? (() => {
                                                      const v = Number(
                                                        d.receivedShelfLifePercent,
                                                      );
                                                      return `${Number.isInteger(v) ? v : v.toFixed(2)}%`;
                                                    })()
                                                  : "-"}
                                              </td>
                                              <td style={{ padding: "10px", textAlign: "center" }}>{d.damaged ?? 0}</td>
                                              <td style={{ padding: "10px", textAlign: "center" }}>{formatDate(d.expiryDate)}</td>
                                            </tr>
                                          ),
                                        )}
                                      </tbody>
                                      </>
                                      );
                                    })()}
                                    </table>
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <style>{`
        .reminder-input{
          padding:11px 12px;
          border-radius:8px;
          border:1px solid #d1d5db;
          font-size:14px;
          outline:none;
          height:40px;
          width:100%;
          box-sizing:border-box;
          background:white;
          color:black;
        }

        .reminder-input:focus{
          border-color:#75BFB0;
          box-shadow:0 0 0 2px rgba(117,191,176,0.2);
        }

        .th{
          padding:12px;
          text-align:left;
          font-weight:600;
        }

        .td{
          padding:12px;
        }

        .loader {
          border: 4px solid #f3f3f3;
          border-top: 4px solid #75BFB0;
          border-radius: 50%;
          width: 35px;
          height: 35px;
          animation: spin 1s linear infinite;
          margin: auto;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        /* DARK MODE */
        [data-mui-color-scheme="dark"] .reminder-input {
          background: #0f172a;
          color: #e5e7eb;
          border: 1px solid #334155;
        }

        [data-mui-color-scheme="dark"] .reminder-input::placeholder {
          color: #9ca3af;
        }
      `}</style>
    </div>
  );
};

export default GrnRecords;
